/**
 * CallToActionSection Component
 *
 * CTA buttons for account creation/sign-in with referral token navigation.
 * Integrates with React Router for navigation and follows BiteWise component patterns.
 */

import React from 'react';
import { useNavigate } from 'react-router';
import { useTheme } from '../../hooks/useTheme';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { TitleText } from '../ui/TitleText';
import { BodyText } from '../ui/BodyText';
import type { CallToActionSectionProps, ReferralNavigationState } from '../../types/referralLanding';

/**
 * CallToActionSection component for referral landing page actions
 *
 * @param props - CallToActionSection component props
 * @returns JSX.Element
 */
export const CallToActionSection: React.FC<CallToActionSectionProps> = ({
  referralToken,
  physicianName,
  dieticianName,
  className = '',
}) => {
  const { isDark } = useTheme();
  const navigate = useNavigate();

  const cardBgColor = isDark ? 'bg-secondary-800' : 'bg-white';
  const borderColor = isDark ? 'border-secondary-700' : 'border-secondary-200';

  /**
   * Handle navigation to patient registration
   */
  const handleCreateAccount = () => {
    const navigationState: ReferralNavigationState = {
      referralToken,
      physicianName,
      dieticianName,
    };

    navigate('/patient/register', {
      state: navigationState,
      replace: false
    });
  };

  /**
   * Handle navigation to sign in
   */
  const handleSignIn = () => {
    const navigationState: ReferralNavigationState = {
      referralToken,
      physicianName,
      dieticianName,
    };

    navigate('/login', {
      state: navigationState,
      replace: false
    });
  };

  return (
    <Card className={`${cardBgColor} ${borderColor} ${className}`}>
      <div className="p-6 text-center" data-testid="call-to-action-section">
        {/* Header */}
        <div className="mb-6">
          <TitleText level={2} className="mb-3">
            Get Started with Your Care
          </TitleText>

          <BodyText variant="secondary" className="max-w-md mx-auto">
            Create your account to connect with {dieticianName} and begin your personalised nutrition journey.
          </BodyText>
        </div>

        {/* Action buttons */}
        <div className="space-y-4">
          {/* Primary CTA - Create Account */}
          <Button
            onClick={handleCreateAccount}
            variant="primary"
            size="lg"
            className="w-full sm:w-auto sm:min-w-[200px]"
            aria-label={`Create account with referral from ${physicianName}`}
          >
            <UserPlusIcon className="w-5 h-5 mr-2" />
            Create Patient Account
          </Button>

          {/* Secondary CTA - Sign In */}
          <div className="flex flex-col items-center space-y-2">
            <BodyText size="sm" variant="secondary">
              Already have an account?
            </BodyText>

            <Button
              onClick={handleSignIn}
              variant="outline"
              size="md"
              className="w-full sm:w-auto sm:min-w-[160px]"
              aria-label={`Sign in to existing account with referral from ${physicianName}`}
            >
              <LoginIcon className="w-4 h-4 mr-2" />
              Login
            </Button>
          </div>
        </div>

        {/* Additional information */}
        <div className={`mt-6 pt-6 border-t ${isDark ? 'border-secondary-700' : 'border-secondary-200'}`}>
          <div className="flex items-center justify-center space-x-2 mb-3">
            <ShieldCheckIcon className="w-5 h-5 text-green-500" />
            <BodyText size="sm" className="font-medium">
              Secure & HIPAA Compliant
            </BodyText>
          </div>

          <BodyText size="sm" variant="secondary" className="max-w-sm mx-auto">
            Your health information is protected with enterprise-grade security and follows all HIPAA compliance standards.
          </BodyText>
        </div>
      </div>
    </Card>
  );
};

/**
 * User Plus icon component
 */
const UserPlusIcon: React.FC<{ className?: string }> = ({ className = '' }) => (
  <svg
    className={className}
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z"
    />
  </svg>
);

/**
 * Login icon component
 */
const LoginIcon: React.FC<{ className?: string }> = ({ className = '' }) => (
  <svg
    className={className}
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h7a3 3 0 013 3v1"
    />
  </svg>
);

/**
 * Shield Check icon component
 */
const ShieldCheckIcon: React.FC<{ className?: string }> = ({ className = '' }) => (
  <svg
    className={className}
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
    />
  </svg>
);

export default CallToActionSection;